import Stack from "@mui/material/Stack";
import ExplorerTableLink from "../ExplorerTableLink";
import { HeaderProps } from "./Header/types";
import TeamHeaderBand from "./TeamHeaderBand";
import { TeamRankingsProps } from "./TeamRankings/types";
import TeamReportDataTable from "../TeamReportDataTable";
import { SHOT_QUAL_TS_LABELS } from "../ShotQualLabel";
import { TeamReportDataTableProps } from "../TeamReportDataTable/types";
import { Box } from "@mui/material";
import RotationMap from "./RotationMap";
import { PlayerRotationChartProps } from "./PlayerRotationChart/types";
import { GameLogItemProps } from "../GameLogItem/types";
import GameLogCards from "../GameLogItem/GameLogCards";
import TeamProfileStrip, { TeamProfileData } from "./TeamProfileStrip";
import { TeamReportList } from "../../util/Requests";
import { enrichGameLogWithIds } from "./utils";
import ChallengesTable from "../ChallengesTable";
import { gameScope, garbageLine, garbageMinute } from "../OwnershipPostGamePage/utils";
import { useChallengeQuery } from "../ChallengesTable/useChallengeQuery";
import "../teamReportDesign.scss";

export default function OverviewPage({
  headerProps,
  rankingsProps,
  teamOverviewTableData,
  playerRotations,
  gameLog,
  reportList,
  isSingleGame = false,
  availableGameIds = [],
  teamId,
  season,
  seasonType,
  teamProfile,
}: {
  headerProps: HeaderProps;
  rankingsProps: TeamRankingsProps;
  teamOverviewTableData: TeamReportDataTableProps;
  playerRotations: PlayerRotationChartProps;
  gameLog: GameLogItemProps[];
  /** Used to resolve game-report ids for the game log cards */
  reportList?: TeamReportList;
  isSingleGame?: boolean;
  availableGameIds?: string[];
  teamId?: string;
  season?: string;
  seasonType?: string;
  /** Overview Team Profile strip data (blob "Team Profile" section) */
  teamProfile?: TeamProfileData | null;
}) {
  // Game reports carry the coach's challenges under the score (Trevor,
  // 2026-09-30); a season report asks for nothing.
  const gameId = headerProps.game?.id ?? headerProps.game?.game_id;
  const challenges = useChallengeQuery(gameId, isSingleGame);
  // Garbage time is the coaches' (the ownership view leaves it out, Trevor 2026-10-10): a line under the score and
  // a marker across the rotation map
  const garbage = isSingleGame
    ? garbageLine(headerProps.gameInfo, headerProps.teamAbbrev)
    : null;
  const garbageAt = isSingleGame ? garbageMinute(headerProps.gameInfo) : null;

  const gameLogWithIds = enrichGameLogWithIds(
    gameLog,
    reportList,
    headerProps.teamAbbrev,
    availableGameIds
  );

  return (
    <Stack
      sx={{
        ".MuiTypography-h4, .MuiTypography-h5": {
          fontFamily: '"Roboto Condensed", sans-serif',
        },
      }}
    >
      <TeamHeaderBand
        headerProps={headerProps}
        rankingsProps={rankingsProps}
        isSingleGame={isSingleGame}
        // A game blob carries the schedule row, not the report season: name the game's own season and type, as the
        // ownership view does (a preseason game read "Regular Season", TR6 2026-10-10)
        scope={isSingleGame ? gameScope(headerProps.game) : undefined}
        cardNote={
          isSingleGame && headerProps.gameInfo ? (
            <>
              <ChallengesTable
                inline
                teams={[headerProps.teamAbbrev, headerProps.gameInfo.opponent]}
                status={challenges.status}
                challenges={challenges.status === "ok" ? challenges.data : undefined}
              />
              {garbage && <div>{garbage}</div>}
            </>
          ) : undefined
        }
      />

      <Stack spacing={3} sx={{ mt: 1 }}>
        <Box>
          {/* No "Overall" header row — the row labels say it, and the
              legend lives in the header band's .ph strip (vertical-space
              saver, user call 2026-08-28). */}
          {teamOverviewTableData.explorer && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: -0.5, "@media print": { display: "none" } }}>
              <ExplorerTableLink href={teamOverviewTableData.explorer} fontSize="1rem" />
            </Box>
          )}
          <Box
            sx={{
              ".MuiTableBody-root > .MuiTableRow-root:nth-of-type(2) > .MuiTableCell-root":
                {
                  borderBottom: "3px double rgba(0,0,0,0.4)",
                },
            }}
          >
            <TeamReportDataTable
              {...teamOverviewTableData}
              headerLabels={SHOT_QUAL_TS_LABELS}
              teamId={teamId}
              season={season}
              seasonType={seasonType}
              rows={teamOverviewTableData.rows.map((row) => ({
                ...row,
                shotDistributionContext:
                  typeof row.name === "string" &&
                  row.name.toLowerCase().includes("offense")
                    ? "offense"
                    : "defense",
              }))}
            />
          </Box>
        </Box>
        {gameLogWithIds.length > 0 && (
          <Box>
            <Box className="trd">
              <div className="sh pg">
                <span className="ttl">Game Log</span>
                <span className="nt">Last 5 · cards open the game report</span>
              </div>
            </Box>
            <GameLogCards games={gameLogWithIds} />
          </Box>
        )}
        {/* Team Profile strip — the identity card in the slot the shot
            charts vacated (they moved to the HC page tops, 2026-09-10).
            Game blobs carry it with percentiles since TR4; older game blobs
            have none and the strip renders nothing. */}
        <TeamProfileStrip profile={teamProfile} />
        <RotationMap
          {...playerRotations}
          isSingleGame={isSingleGame}
          marker={
            garbageAt !== null
              ? { minute: garbageAt, label: "Garbage time" }
              : undefined
          }
        />
      </Stack>
    </Stack>
  );
}
